// T-039 / T-062 — GDPR Art. 20 data export.
// Builds a ZIP from local Dexie rows merged with pending outbox payloads.
// When online, a full server pull is performed first via the raw PocketBase client.

import { strToU8, zip } from "fflate";
import { getDb, type SyncedRow } from "./db";
import { sync } from "./sync";
import { getAuthService } from "./auth";
import { EXERCISE_IDS } from "../data/__generated__/exerciseIds";

const EXPORT_COLLECTIONS = [
  "workout_logs",
  "log_entries",
  "user_progress",
  "progress_photos",
  "community_posts",
  "post_comments",
  "post_likes",
  "social_follows",
  "coach_athletes",
  "coach_notes",
];

const COLLECTION_FILTERS: Record<string, (uid: string) => string> = {
  workout_logs: (uid) => `owner = "${uid}"`,
  log_entries: (uid) => `owner = "${uid}"`,
  user_progress: (uid) => `owner = "${uid}"`,
  progress_photos: (uid) => `owner = "${uid}"`,
  community_posts: (uid) => `owner = "${uid}"`,
  post_comments: (uid) => `owner = "${uid}"`,
  post_likes: (uid) => `owner = "${uid}"`,
  social_follows: (uid) => `owner = "${uid}" || target = "${uid}"`,
  coach_athletes: (uid) => `athlete = "${uid}" || coach = "${uid}"`,
  coach_notes: (uid) => `owner = "${uid}" || athlete = "${uid}"`,
};

const FILE_FIELDS: Record<string, string[]> = {
  progress_photos: ["photo"],
  users: ["avatar"],
};

export type SyncTag = "unsynced" | "server" | "synced";
export type Completeness = "complete" | "partial" | "local-only";

export interface ExportBundle {
  userId: string;
  generatedAt: string;
  appBuild: string;
  completeness: Completeness;
  failedCollections?: string[];
  data: Record<string, ExportRow[]>;
  files?: Record<string, Uint8Array | { offline: true }>;
}

export interface ExportRow extends Record<string, unknown> {
  id: string;
  owner?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  sync: SyncTag;
}

export interface BuildExportOptions {
  onProgress?: (stage: string, count: number, total: number) => void;
}

function isOnline(): boolean {
  if (typeof navigator === "undefined") return false;
  return navigator.onLine !== false;
}

function localTable(name: string): { toArray(): Promise<SyncedRow[]> } {
  const db = getDb();
  return (db as unknown as Record<string, { toArray(): Promise<SyncedRow[]> }>)[name] as {
    toArray(): Promise<SyncedRow[]>;
  };
}

async function queuedIdsByCollection(): Promise<Record<string, Set<string>>> {
  const out: Record<string, Set<string>> = {};
  const queue = await getDb()
    .syncQueue.where("status")
    .anyOf("pending", "failed")
    .toArray();
  for (const entry of queue) {
    (out[entry.collection] ??= new Set()).add(entry.recordId);
  }
  return out;
}

async function localRows(userId: string): Promise<Record<string, ExportRow[]>> {
  const queued = await queuedIdsByCollection();
  const out: Record<string, ExportRow[]> = {};
  for (const col of EXPORT_COLLECTIONS) {
    const table = localTable(col);
    let rows = await table.toArray();
    if (col === "coach_notes") {
      rows = rows.filter(
        (r) =>
          (r as { owner?: string }).owner === userId ||
          (r as { athlete?: string }).athlete === userId,
      );
    } else if (col === "social_follows") {
      rows = rows.filter(
        (r) =>
          (r as { owner?: string }).owner === userId ||
          (r as { target?: string }).target === userId,
      );
    } else if (col === "coach_athletes") {
      rows = rows.filter(
        (r) =>
          (r as { coach?: string }).coach === userId ||
          (r as { athlete?: string }).athlete === userId,
      );
    } else {
      rows = rows.filter((r) => (r as SyncedRow).owner === userId);
    }
    const queuedSet = queued[col] ?? new Set<string>();
    out[col] = rows.map((r) => ({
      ...(r as Record<string, unknown>),
      id: (r as { id: string }).id,
      sync: queuedSet.has((r as { id: string }).id) ? ("unsynced" as SyncTag) : ("synced" as SyncTag),
    }));
  }
  return out;
}

function mergeRow(
  merged: Map<string, ExportRow>,
  id: string,
  incoming: ExportRow,
  source: SyncTag,
): void {
  const existing = merged.get(id);
  if (!existing) {
    merged.set(id, { ...incoming, sync: source });
    return;
  }
  if (source === "unsynced") {
    // Local pending edit wins because it has not been accepted yet.
    merged.set(id, { ...incoming, sync: "unsynced" });
  } else if (existing.sync === "unsynced") {
    // Keep the pending payload, but mark it as also present on server.
    merged.set(id, { ...existing, sync: "synced" });
  } else {
    merged.set(id, { ...incoming, sync: "synced" });
  }
}

async function mergeQueueInto(
  local: Record<string, ExportRow[]>,
  userId: string,
): Promise<void> {
  const queue = await getDb()
    .syncQueue.where("status")
    .anyOf("pending", "failed")
    .toArray();
  for (const entry of queue) {
    const payload = entry.payload ? (JSON.parse(entry.payload) as SyncedRow) : null;
    if (!payload || payload.owner !== userId) continue;
    const list = local[entry.collection] ?? [];
    const map = new Map(list.map((r) => [r.id, r]));
    mergeRow(map, entry.recordId, { ...payload, sync: "unsynced" }, "unsynced");
    local[entry.collection] = Array.from(map.values());
  }
}

async function fetchServerCollection(
  col: string,
  userId: string,
  opts: BuildExportOptions,
): Promise<ExportRow[]> {
  const raw = getAuthService().getRawClient?.();
  if (!raw) throw new Error("no raw client");
  const filterFn = COLLECTION_FILTERS[col];
  if (!filterFn) throw new Error(`unknown collection ${col}`);
  const rows: ExportRow[] = [];
  let page = 1;
  while (true) {
    const collection = (raw as unknown as { collection: (name: string) => unknown }).collection(col) as {
      getList(page: number, perPage: number, opts: Record<string, unknown>): Promise<{ items: unknown[]; totalItems: number }>;
    };
    const result = await collection.getList(page, 500, {
      filter: filterFn(userId),
      sort: "updated",
    });
    const items = (result.items as Record<string, unknown>[]).map((r) => ({
      ...r,
      sync: "server" as SyncTag,
    })) as ExportRow[];
    rows.push(...items);
    opts.onProgress?.("collect", rows.length, result.totalItems);
    if (rows.length >= result.totalItems) break;
    page += 1;
  }
  return rows;
}

async function fullServerPull(
  userId: string,
  opts: BuildExportOptions,
): Promise<{ data: Record<string, ExportRow[]>; failed: string[] }> {
  const out: Record<string, ExportRow[]> = {};
  const failed: string[] = [];
  for (const col of EXPORT_COLLECTIONS) {
    try {
      out[col] = await fetchServerCollection(col, userId, opts);
    } catch (err) {
      failed.push(col);
    }
  }
  return { data: out, failed };
}

async function fetchUserRecord(userId: string): Promise<ExportRow | undefined> {
  const raw = getAuthService().getRawClient?.();
  if (!raw) return undefined;
  const record = raw.authStore.record as Record<string, unknown> | undefined;
  let body: Record<string, unknown> = {};
  try {
    body = (await raw.collection("users").getOne(userId)) as Record<string, unknown>;
  } catch {
    body = {};
  }
  return {
    id: userId,
    email: (record?.email ?? body.email) as string | undefined,
    name: (record?.name ?? body.name) as string | undefined,
    role: (record?.role ?? body.role) as string | undefined,
    bio: (record?.bio ?? body.bio) as string | undefined,
    locale: (record?.locale ?? body.locale) as string | undefined,
    settings: (record?.settings ?? body.settings) as unknown,
    invite_code: (record?.invite_code ?? body.invite_code) as string | undefined,
    // A1 audit note: prefer the fresh `body` for FILE fields. The cached
    // auth-store `record` may name a stale filename if the avatar was
    // changed on another device after this session's login; the download
    // would 404 and the archive would carry the "not-available-offline"
    // placeholder. The fresh `getOne` is the source of truth for files.
    avatar: (body.avatar ?? record?.avatar) as string | undefined,
    created: (record?.created ?? body.created) as string | undefined,
    sync: "server",
  };
}

async function getFileToken(raw: { authStore: { token: string }; baseUrl: string }): Promise<string> {
  const url = new URL("/api/files/token", raw.baseUrl).toString();
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${raw.authStore.token}` },
  });
  if (!res.ok) throw new Error(`file token ${res.status}`);
  const data = (await res.json()) as { token?: string };
  if (!data.token) throw new Error("file token missing");
  return data.token;
}

function fileFolder(collection: string): string {
  if (collection === "progress_photos") return "photos";
  if (collection === "users") return "avatar";
  return "media";
}

function filePath(collection: string, id: string, filename: string): string {
  const ext = filename.includes(".") ? filename.split(".").pop() : "bin";
  return `${fileFolder(collection)}/${id}.${ext}`;
}

async function downloadFile(
  raw: { authStore: { token: string }; baseUrl: string },
  collection: string,
  id: string,
  filename: string,
  token: string,
): Promise<Uint8Array> {
  const url = new URL(`/api/files/${collection}/${id}/${filename}`, raw.baseUrl).toString();
  const res = await fetch(`${url}?token=${encodeURIComponent(token)}`, {
    headers: { Authorization: `Bearer ${raw.authStore.token}` },
  });
  if (!res.ok) throw new Error(`download ${res.status}`);
  const blob = await res.blob();
  const ab = await blob.arrayBuffer();
  return new Uint8Array(ab);
}

async function fetchAttachments(
  data: Record<string, ExportRow[]>,
  _userId: string,
): Promise<Record<string, Uint8Array | { offline: true }>> {
  const files: Record<string, Uint8Array | { offline: true }> = {};
  const online = isOnline();
  const raw = getAuthService().getRawClient?.();
  if (!raw || !online) {
    for (const col of Object.keys(FILE_FIELDS)) {
      for (const row of data[col] ?? []) {
        for (const field of FILE_FIELDS[col]) {
          const filename = row[field] as string | undefined;
          if (filename) files[filePath(col, row.id, filename)] = { offline: true };
        }
      }
    }
    return files;
  }

  let token = "";
  try {
    token = await getFileToken(raw);
  } catch {
    // If the token endpoint fails, mark all files offline rather than abort the export.
  }

  for (const col of Object.keys(FILE_FIELDS)) {
    for (const row of data[col] ?? []) {
      for (const field of FILE_FIELDS[col]) {
        const filename = row[field] as string | undefined;
        if (!filename) continue;
        const path = filePath(col, row.id, filename);
        if (!token) {
          files[path] = { offline: true };
          continue;
        }
        try {
          files[path] = await downloadFile(raw, col, row.id, filename, token);
        } catch {
          files[path] = { offline: true };
        }
      }
    }
  }
  return files;
}

export async function buildExport(
  userId: string,
  opts: BuildExportOptions = {},
): Promise<ExportBundle> {
  const online = isOnline();
  let server: Record<string, ExportRow[]> | undefined;
  let failedCollections: string[] | undefined;

  if (online) {
    try {
      opts.onProgress?.("sync", 0, 0);
      await sync.syncNow();
      const pulled = await fullServerPull(userId, opts);
      server = pulled.data;
      failedCollections = pulled.failed;
    } catch {
      // fall through to local-only
    }
  }

  const local = await localRows(userId);
  await mergeQueueInto(local, userId);

  const merged: Record<string, ExportRow[]> = {};
  for (const col of EXPORT_COLLECTIONS) {
    const map = new Map<string, ExportRow>();
    for (const r of server?.[col] ?? []) {
      mergeRow(map, r.id, r, "server");
    }
    for (const r of local[col] ?? []) {
      mergeRow(map, r.id, r, r.sync);
    }
    merged[col] = Array.from(map.values());
  }

  const user = await fetchUserRecord(userId);
  if (user) merged.users = [user];

  // Add exerciseSlug to log_entries.
  const entries = merged["log_entries"];
  if (entries) {
    const reverse = Object.fromEntries(
      Object.entries(EXERCISE_IDS).map(([slug, id]) => [id, slug]),
    );
    for (const e of entries) {
      const exId = e.exercise as string | undefined;
      if (exId) e.exerciseSlug = reverse[exId];
    }
  }

  let completeness: Completeness;
  if (!online || (!server && failedCollections === undefined)) {
    completeness = "local-only";
  } else if (failedCollections && failedCollections.length > 0) {
    completeness = "partial";
  } else {
    completeness = "complete";
  }

  const files = await fetchAttachments(merged, userId);

  return {
    userId,
    generatedAt: new Date().toISOString(),
    appBuild: (import.meta.env.VITE_APP_BUILD as string | undefined) ?? "dev",
    completeness,
    failedCollections,
    data: merged,
    files,
  };
}

export function rowsToCsv(rows: ExportRow[]): string {
  if (rows.length === 0) return "\uFEFFid\n";
  const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r)))).filter((k) => k !== "sync");
  const header = "\uFEFF" + keys.join(",") + ",sync\n";
  const lines = rows.map((r) => {
    const cells = keys.map((k) => {
      const v = r[k];
      if (v == null) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    });
    cells.push(r.sync);
    return cells.join(",");
  });
  return header + lines.join("\n") + "\n";
}

export function buildReadme(
  completeness: Completeness,
  locale = "en",
  failedCollections: string[] = [],
): string {
  type LocalizedLines = { en: string[]; de: string[]; hu: string[] };
  const partialNote = {
    en: `WARNING: Some server data could not be fetched (${failedCollections.join(", ")}). The archive may be incomplete; please retry while connected.`,
    de: `WARNUNG: Einige Serverdaten konnten nicht abgerufen werden (${failedCollections.join(", ")}). Das Archiv ist möglicherweise unvollständig; bitte wiederholen Sie den Export mit Verbindung.`,
    hu: `FIGYELMEZTETÉS: Néhány szerveradatot nem sikerült lekérni (${failedCollections.join(", ")}). Az archívum hiányos lehet; ismételje meg internetkapcsolattal.`,
  }[locale] ?? "";

  const lines: LocalizedLines = {
    en: [
      "CaliCool personal data export",
      completeness === "local-only"
        ? "WARNING: This export was created while offline and contains only the data stored on this device. Rows created on other devices may be missing; please rerun the export while connected to the internet for a complete copy."
        : completeness === "partial"
          ? partialNote
          : "This export contains all personal data CaliCool holds for this account at the time of generation.",
      "Included: workouts, log entries, progress, photos, posts, comments, likes, follows, coach links and notes about you.",
      "Not included: server logs (retained up to 7 days) and encrypted backups (retained up to 30 days).",
      "Files: data.json (machine-readable), data.csv/*.csv (spreadsheet), photos/ and media/ (attached files).",
    ],
    de: [
      "CaliCool Export persönlicher Daten",
      completeness === "local-only"
        ? "WARNUNG: Dieser Export wurde offline erstellt und enthält nur die auf diesem Gerät gespeicherten Daten. Daten, die auf anderen Geräten erstellt wurden, können fehlen; bitte wiederholen Sie den Export mit Internetverbindung für eine vollständige Kopie."
        : completeness === "partial"
          ? partialNote
          : "Dieser Export enthält alle persönlichen Daten, die CaliCool zum Zeitpunkt der Erstellung für dieses Konto gespeichert hat.",
      "Enthalten: Workouts, Satzeinträge, Fortschritt, Fotos, Beiträge, Kommentare, Likes, Follows, Coach-Verknüpfungen und Notizen über Sie.",
      "Nicht enthalten: Server-Logs (bis zu 7 Tage) und verschlüsselte Backups (bis zu 30 Tage).",
      "Dateien: data.json (maschinenlesbar), data.csv/*.csv (Tabellenkalkulation), photos/ und media/ (Anhänge).",
    ],
    hu: [
      "CaliCool személyes adatok exportja",
      completeness === "local-only"
        ? "FIGYELMEZTETÉS: Ez az export offline állapotban készült, és csak ezen az eszközön tárolt adatokat tartalmaz. Más eszközökön létrehozott sorok hiányozhatnak; a teljes másolathoz ismételje meg az exportot internetkapcsolattal."
        : completeness === "partial"
          ? partialNote
          : "Ez az export tartalmazza az összes olyan személyes adatot, amelyet a CaliCool a létrehozás időpontjában tárolt ehhez a fiókhoz.",
      "Tartalmazza: edzések, sorozatbejegyzések, haladás, fényképek, bejegyzések, hozzászólások, lájkok, követések, coach-kapcsolatok és Önről készült jegyzetek.",
      "Nem tartalmazza: szervernaplók (legfeljebb 7 napig) és titkosított biztonsági mentések (legfeljebb 30 napig).",
      "Fájlok: data.json (géppel olvasható), data.csv/*.csv (táblázat), photos/ és media/ (csatolt fájlok).",
    ],
  };
  const order = [locale, "en", "de", "hu"] as const;
  const result: string[] = [];
  for (const l of order) {
    const entry = lines[l as keyof LocalizedLines];
    if (entry) {
      result.push(entry.join("\n"));
      result.push("");
    }
  }
  return result.join("\n");
}

export async function bundleToZip(
  bundle: ExportBundle,
  attachments: Record<string, Uint8Array | { offline: true }> = bundle.files ?? {},
): Promise<Blob> {
  const files: Record<string, Uint8Array> = {};
  files["README.txt"] = strToU8(buildReadme(bundle.completeness, "en", bundle.failedCollections ?? []));
  files["data.json"] = strToU8(JSON.stringify(bundle, null, 2));
  for (const [col, rows] of Object.entries(bundle.data)) {
    if (rows.length > 0) {
      files[`data.csv/${col}.csv`] = strToU8(rowsToCsv(rows));
    }
  }
  for (const [path, content] of Object.entries(attachments)) {
    files[path] = content instanceof Uint8Array ? content : strToU8("not-available-offline");
  }
  return new Promise((resolve, reject) => {
    zip(files, { level: 6 }, (err, data) => {
      if (err) reject(err);
      else resolve(new Blob([data], { type: "application/zip" }));
    });
  });
}

export function downloadZip(blob: Blob, userId: string): void {
  const date = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `calicool-export-${date}-${userId}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
